import { useCallback } from 'react';

import type {
  ProjectCompaniesSort,
  ProjectListing,
} from '@/api/v4/projects.api';
import { QueryKey } from '@/config/query-client';
import { ListingStageName } from '@/features/listing-stage/types';
import type { ISort } from '@/ui/table/table.types';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { orderBy } from 'lodash-es';
import { useQueryClient } from '@tanstack/react-query';

import { projectListingSortState } from '../../project-listing.state';
import { projectIdState } from '../../project.state';
import { useProjectListings } from '../use-project-listings.query';

import type { TabStage } from './use-stage-tabs-config';

const sortKeysHandledByFrontend: Array<Partial<ProjectCompaniesSort>> = [
  'companyName',
  'yearEstablished',
  'capitalRaised',
  'averageRating',
];

export const useSortStageListings = () => {
  const queryClient = useQueryClient();
  const projectId = useAtomValue(projectIdState);
  const { projectListings } = useProjectListings({
    projectId,
  });
  const [sortData, setSortData] = useAtom(projectListingSortState);
  const setProjectListingSort = useSetAtom(projectListingSortState);
  const resetProjectListingSort = useCallback(
    (tab: TabStage) => {
      setProjectListingSort(
        tab.name === ListingStageName.deal
          ? { direction: 'asc', sort: 'status' }
          : { direction: 'desc', sort: 'createdDate' },
      );
    },
    [setProjectListingSort],
  );

  const getOrganizationKey = (sortKey: ProjectCompaniesSort) => {
    if (sortKey === 'companyName') {
      return 'name';
    }

    return sortKey;
  };

  const handleStageContentSort = ({
    sort,
    direction,
  }: ISort<ProjectCompaniesSort>) => {
    if (sortKeysHandledByFrontend.includes(sort)) {
      const sortedData = orderBy(
        projectListings,
        listing =>
          listing.organization[
            getOrganizationKey(sort) as keyof ProjectListing['organization']
          ],
        direction,
      );

      queryClient.setQueryData<ProjectListing[]>(
        [QueryKey.ProjectListings, projectId, { sort, direction }],
        sortedData,
      );
    }

    return setSortData({ sort, direction });
  };

  return {
    resetProjectListingSort,
    sortData,
    handleStageContentSort,
  };
};
